"use client";
import React from "react";
import { ButtonCta } from "@/components";
import { SectionHeading } from "@/components/common/decor";

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
    <path d={d} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const services = [
  {
    title: "Self-Publishing & Book Publishing",
    desc: "Manuscript to retailer-ready file — ISBN, formatting, and full distribution setup, managed start to finish.",
    img: "/images/sec4-image.webp",
    icon: "M4 5h7a3 3 0 0 1 3 3v11a3 3 0 0 0-3-3H4V5Zm16 0h-7a3 3 0 0 0-3 3v11a3 3 0 0 1 3-3h7V5Z",
    span: "md:col-span-2 md:row-span-2",
    big: true,
  },
  {
    title: "Ghostwriting Services",
    desc: "Experienced ghostwriters shape your ideas into a complete manuscript that still sounds authentically like you.",
    img: "/images/book-covers/hero-slide-8.webp",
    icon: "M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z",
    span: "md:row-span-2",
    big: true,
  },
  {
    title: "Book Editing Services",
    desc: "Developmental, line, and proofreading passes from real editors — never automated or templated.",
    img: "/images/book-covers/hero-slide-6.webp",
    icon: "M4 7h16M4 12h10M4 17h7",
  },
  {
    title: "Book Marketing & Author Branding",
    desc: "Launch campaigns, author websites, and press outreach that help readers find you and remember you.",
    img: "/images/book-covers/hero-slide-2.webp",
    icon: "M3 11l18-7-7 18-2.5-7.5L3 11Z",
  },
  {
    title: "Children's Book Publishing",
    desc: "Age-appropriate storytelling paired with original illustration and picture-book-quality layout.",
    img: "/images/book-covers/hero-slide-10.webp",
    icon: "M12 7c-1.5-2-4-2-6-1v11c2-1 4.5-1 6 1 1.5-2 4-2 6-1V6c-2-1-4.5-1-6 1Z",
  },
  {
    title: "Book Cover & Interior Design",
    desc: "Retail-ready covers and clean, bookstore-quality interior formatting for print and digital.",
    img: "/images/book-covers/hero-slide-4.webp",
    icon: "M4 4h16v16H4zM4 9h16M9 9v11",
  },
  {
    title: "Audiobook Production",
    desc: "Professional narration, mixing, and mastering that bring your story to listeners everywhere.",
    img: "/images/book-covers/hero-slide-14.webp",
    icon: "M4 13v-1a8 8 0 0 1 16 0v1M4 13a2 2 0 0 0-2 2v1a2 2 0 0 0 2 2h1v-5H4Zm16 0a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2h-1v-5h1Z",
  },
  {
    title: "Literary Consulting & Manuscript Evaluation",
    desc: "An honest, expert read on your manuscript — what's working, and what it still needs before it's ready.",
    img: "/images/book-covers/hero-slide-12.webp",
    icon: "M12 3l2.5 5 5.5.8-4 3.9.9 5.5L12 18l-4.9 2.6.9-5.5-4-3.9L10 8l2-5Z",
  },
];

export default function ServicesBento() {
  return (
    <section className="relative bg-paper px-4 py-20 md:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHeading
            eyebrow="Our Book Publishing Services"
            title="Every Step of Getting Your Book Published"
            align="left"
            className="!max-w-xl"
          />
          <div className="hidden md:block">
            <ButtonCta buttonText="Request a Quote" classStyle="text-white bg-brand-600 hover:bg-brand-700 h-12" />
          </div>
        </div>

        <div className="grid auto-rows-[260px] grid-cols-1 gap-5 md:auto-rows-[232px] md:grid-cols-3">
          {services.map((s, i) => (
            <article
              key={s.title}
              data-aos="fade-up"
              data-aos-delay={(i % 3) * 90}
              className={`group relative overflow-hidden rounded-3xl border border-ink/10 bg-ink ${s.span || ""}`}
            >
              <img loading="lazy" decoding="async"
                src={s.img}
                alt={s.title}
                className="absolute inset-0 h-full w-full object-cover opacity-60 transition-all duration-700 group-hover:scale-110 group-hover:opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-transparent" />
              <div className="relative flex h-full flex-col justify-end p-6">
                <span className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gold/90 text-ink">
                  <Icon d={s.icon} />
                </span>
                <h3 className={`font-display font-semibold text-white ${s.big ? "text-2xl" : "text-xl"}`}>
                  {s.title}
                </h3>
                <p className={`mt-2 text-sm leading-relaxed text-white/75 ${s.big ? "" : "line-clamp-2"}`}>
                  {s.desc}
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-gold-soft opacity-0 transition-all duration-300 group-hover:opacity-100">
                  Learn more
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 flex justify-center md:hidden">
          <ButtonCta buttonText="Request a Quote" classStyle="text-white bg-brand-600 hover:bg-brand-700 h-12" />
        </div>
      </div>
    </section>
  );
}
